import type { ApiResponse } from '@/shared/types';
import api from '@/shared/api/client';

export type QuotaPlanRecord = Record<string, unknown>;
export type UsageRecord = Record<string, unknown>;
export type PlanVersionRecord = Record<string, unknown>;
export type SubscriptionRecord = Record<string, unknown>;
export type InvoiceRecord = Record<string, unknown>;

export const operatorBillingApi = {
  listQuotaPlans: (params?: { includeInactive?: boolean }) =>
    api.get<ApiResponse<QuotaPlanRecord[]>>('/operator/quota-plans', { params }),

  createQuotaPlan: (data: Record<string, unknown>) =>
    api.post<ApiResponse<QuotaPlanRecord>>('/operator/quota-plans', data),

  updateQuotaPlan: (id: string, data: Record<string, unknown>) =>
    api.put<ApiResponse<QuotaPlanRecord>>(`/operator/quota-plans/${id}`, data),

  deleteQuotaPlan: (id: string) =>
    api.delete<ApiResponse<{ success: boolean }>>(`/operator/quota-plans/${id}`),

  getUsage: (params: { tenantId: string; startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<UsageRecord[]>>('/operator/usage', { params }),

  listUsageSummaries: (params?: { startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<UsageRecord[]>>('/operator/usage/summaries', { params }),

  listPlanVersions: (quotaPlanId?: string) =>
    api.get<ApiResponse<PlanVersionRecord[]>>('/operator/billing/plan-versions', {
      params: { quotaPlanId },
    }),

  listSubscriptions: (tenantId?: string) =>
    api.get<ApiResponse<SubscriptionRecord[]>>('/operator/billing/subscriptions', {
      params: { tenantId },
    }),

  listInvoices: (tenantId?: string) =>
    api.get<ApiResponse<InvoiceRecord[]>>('/operator/billing/invoices', {
      params: { tenantId },
    }),

  createSubscriptionInvoice: (subscriptionId: string) =>
    api.post<ApiResponse<InvoiceRecord>>(
      `/operator/billing/subscriptions/${subscriptionId}/invoices`,
    ),

  createInvoiceCheckout: (
    invoiceId: string,
    data?: { successUrl?: string; cancelUrl?: string },
  ) =>
    api.post<
      ApiResponse<{
        checkoutSessionId: string;
        url: string;
        amountMinor: number;
        currency: string;
        invoiceId: string;
        tenantId: string;
        externalRef?: string;
      }>
    >(`/operator/billing/invoices/${invoiceId}/checkout`, data || {}),

  updateSubscriptionStatus: (
    subscriptionId: string,
    action: 'cancel-at-period-end' | 'suspend-now' | 'resume',
  ) =>
    api.post<ApiResponse<SubscriptionRecord>>(
      `/operator/billing/subscriptions/${subscriptionId}/status`,
      { action },
    ),

  getDunningState: (tenantId: string) =>
    api.get<ApiResponse<Record<string, unknown> | null>>(
      `/operator/billing/enhancements/dunning/${encodeURIComponent(tenantId)}`,
    ),

  validateCoupon: (
    tenantId: string,
    data: { code: string; amountMinor: number; currency: string },
  ) =>
    api.post<ApiResponse<{ coupon: Record<string, unknown>; discountMinor: number }>>(
      '/operator/billing/enhancements/coupons/validate',
      data,
      { params: { tenantId } },
    ),

  redeemCoupon: (
    tenantId: string,
    data: {
      code: string;
      amountMinor: number;
      currency: string;
      subscriptionId?: string;
      invoiceId?: string;
    },
  ) =>
    api.post<ApiResponse<Record<string, unknown>>>(
      '/operator/billing/enhancements/coupons/redeem',
      data,
      { params: { tenantId } },
    ),

  simulatePlanChange: (
    tenantId: string,
    data: {
      subscriptionId: string;
      targetPlanVersionId: string;
      effectiveAt?: string;
      persist?: boolean;
    },
  ) =>
    api.post<
      ApiResponse<{
        snapshot: Record<string, unknown>;
        record: Record<string, unknown> | null;
      }>
    >('/operator/billing/enhancements/plan-changes/simulate', data, {
      params: { tenantId },
    }),

  getPlanChangeSimulation: (tenantId: string, simulationId: string) =>
    api.get<ApiResponse<Record<string, unknown>>>(
      `/operator/billing/enhancements/plan-changes/simulations/${encodeURIComponent(simulationId)}`,
      { params: { tenantId } },
    ),
};
